/**
 * razmetka.js — ЕДИНСТВЕННЫЙ держатель разбора разметки ответов на фронте.
 * v1.0 (quick-260912-4cp)
 *
 * Откуда взялось: тело `razmetkaVHtml` — дословный перенос функции-разборщика
 * из PublicChatWidget.js, строки 260-298 состояния 4e66f4c5 (до этой правки).
 * Ничего «по дороге» не улучшено нарочно: перенос и улучшение — разные
 * действия, и смешивать их значит потерять возможность сказать, что
 * именно сломалось.
 * Имя той функции здесь не пишется НАРОЧНО: приёмка задачи — `grep` по
 * frontend/src, и он обязан давать ровно ноль. Ноль с оговоркой «кроме
 * одного комментария» перестаёт быть воротами через месяц.
 *
 * 🔴 Наружу отдаётся ТОЛЬКО санитизированный HTML. Несанитизированной ручки
 *    в экспорте нет — тогда её нельзя забыть позвать. Обещание «не забудем
 *    санитизировать на месте вызова» воротами не является.
 *
 * 🔴 ИЗВЕСТНЫЙ ПРЕДЕЛ перенесённого разбора (не чинится здесь, но замалчивать
 *    его нельзя): ВЫБОР обёртки смотрит на ВЕСЬ исходный текст сразу —
 *    `/^\s*\d+\./m.test(text)`. Если в одном сообщении есть и маркированный,
 *    и нумерованный перечень, то обе обёртки будут `<ol>`, включая ту, что
 *    пришла из маркированного списка. Это косметика нумерации: звёздочек она
 *    обратно не возвращает, содержимое не теряет. Пробы на `ul` против `ol`
 *    здесь поэтому ничего не утверждают.
 *
 * 🔴 А ВОТ ЭТО БЫЛО НЕ КОСМЕТИКОЙ и здесь ПОЧИНЕНО (quick-260912-4cp):
 *    в исходном виде обёртка бралась нежадно по ОДНОМУ пункту —
 *    `/(<li>.*?<\/li>)/gs` — и каждый пункт получал свой собственный `<ol>`.
 *    В браузере перечень из четырёх пунктов выглядел как четыре отдельных
 *    списка, каждый начинающийся с «1.». Счёты `<li>` и `<strong>` этого не
 *    видели: пунктов ровно столько, сколько надо. Увидело показание приёмки —
 *    файл с отрисованным HTML. Теперь обёртка берёт ПОДРЯД идущий пробег
 *    пунктов: `/((?:<li>.*?<\/li>)+)/gs`.
 *
 * Порядок разбора в `razbitNaKuski` обратный привычному и это НАРОЧНО:
 * сначала выделяются `![...](url)`, и только промежутки между ними идут в
 * разбор разметки. Если разбирать разметку первой, правила жирного и курсива
 * покалечат url (подчёркивания и звёздочки внутри адреса).
 */
import DOMPurify from 'dompurify';

// Метка фоновой задачи картинки — служебная, пользователю не показывается.
const FLUX_METKA = /\[FLUX_TASK_ID:[a-f0-9-]+\]/g;
const KARTINKA = /!\[([^\]]*)\]\((.*?)\)/g;

/**
 * Разметка -> HTML, УЖЕ прогнанный через DOMPurify.
 * @param {string} text
 * @returns {string} санитизированный HTML
 */
export function razmetkaVHtml(text) {
    if (!text) return '';
    // Basic Markdown to HTML conversion logic remains the same
    let html = text.replace(/^### (.*$)/gim, '<h3>$1</h3>')
        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        // Handle lists (needs refinement for nested lists)
        .replace(/^\s*([-*+])\s+(.*)/gm, '<li>$2</li>')
        .replace(/^\s*(\d+)\.\s+(.*)/gm, '<li>$2</li>')
        // Wrap consecutive list items in <ul> or <ol>
        // This is a simplified approach, might need a more robust parser for complex cases
        .replace(/<\/li>\s*<li>/g, '</li><li>') // Clean up whitespace between items
        .replace(/((?:<li>.*?<\/li>)+)/gs, (match) => {
            // Check if the original text started with numbers for <ol>
            if (/^\s*\d+\./m.test(text)) {
                // Check if it's already wrapped
                if (!match.startsWith('<ol>')) return '<ol>' + match + '</ol>';
            } else {
                if (!match.startsWith('<ul>')) return '<ul>' + match + '</ul>';
            }
            return match; // Return as is if already wrapped somehow
        })
        // Replace paragraph breaks more carefully
        .replace(/\n\s*\n/g, '</p><p>')
        // Single newlines to <br> only if not part of other tags
        .replace(/([^\n])\n([^\n<])/g, '$1<br/>$2');

    // Wrap the whole thing in <p> tags if no other block elements are present
    if (!html.match(/<\/?(ul|ol|li|h[1-6]|p|blockquote|pre|hr|table)[^>]*>/gi)) {
        html = '<p>' + html + '</p>';
    }
    // Remove any leading/trailing <br/> potentially added incorrectly
    html = html.replace(/^<br\/>|<br\/>$/g, '');
    // Remove <p> tags around lists
    html = html.replace(/<p><(ul|ol)>/g, '<$1>').replace(/<\/(ul|ol)><\/p>/g, '</$1>');

    return DOMPurify.sanitize(html);
}

/**
 * Разбить ответ на куски в том порядке, в каком они стоят в тексте.
 *
 * @param {string} text
 * @returns {Array<{tip:'html', html:string} | {tip:'img', url:string, alt:string} | {tip:'video', url:string}>}
 *   у кусков `tip:'html'` содержимое уже санитизировано (razmetkaVHtml).
 */
export function razbitNaKuski(text) {
    if (!text) return [];
    const chistyj = text.replace(FLUX_METKA, '');
    const kuski = [];
    const re = new RegExp(KARTINKA.source, 'g');
    let posle = 0;
    let match;

    const dobavitTekst = (kusok) => {
        if (!kusok) return;
        const html = razmetkaVHtml(kusok);
        if (html) kuski.push({ tip: 'html', html });
    };

    while ((match = re.exec(chistyj)) !== null) {
        const url = match[2];
        const alt = match[1];
        if (match.index > posle) {
            dobavitTekst(chistyj.substring(posle, match.index));
        }
        if (url.toLowerCase().endsWith('.mp4')) {
            kuski.push({ tip: 'video', url });
        } else {
            kuski.push({ tip: 'img', url, alt });
        }
        posle = match.index + match[0].length;
    }

    if (posle < chistyj.length) {
        dobavitTekst(chistyj.substring(posle));
    }

    return kuski;
}
